import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

import { useWorkflowEditor } from '../WorkflowEditorContext';

import { EdgeInspectorPanel } from './EdgeInspectorPanel';
import { NodeInspectorPanel } from './NodeInspectorPanel';
import { OverviewPanel } from './OverviewPanel';
import { RunsPanel } from './RunsPanel';
import { SchedulesPanel } from './SchedulesPanel';
import { SchemasPanel } from './SchemasPanel';
import { VersionsPanel } from './VersionsPanel';

/**
 * Closed set of right-panel modes the URL can target via `?panel=…`.
 * Adding a new mode: add the literal here, branch on it in `RightPanel`,
 * and surface a button in the tabstrip below. Anything not in this list
 * is rejected by the route's `validateSearch`.
 *
 * `palette` was removed — node-add now lives in the topbar dropdown
 * (`AddNodeDropdown`) so there's a single entry point and one fewer
 * panel switch.
 */
export const rightPanelModes = [
  'overview',
  'node',
  'edge',
  'runs',
  'schedules',
  'schemas',
  'versions',
] as const;
export type RightPanelMode = (typeof rightPanelModes)[number];

const panelLabels: Record<RightPanelMode, string> = {
  overview: 'Overview',
  node: 'Inspector',
  edge: 'Edge',
  runs: 'Runs',
  schedules: 'Schedules',
  schemas: 'Schemas',
  versions: 'Versions',
};

export const managedScheduleTabTitle = 'Managed workflows run from their schedule.';

// One row, never two: tabs that do not fit go into a "More" menu at the end
// (a wrapped row orphaned "VERSIONS" on a second line at panel widths the
// inspector tab makes common).
const tabRow: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  borderBottom: '1px solid var(--border-subtle)',
  gap: 'var(--sp-1)',
  padding: 'var(--sp-1) var(--sp-2)',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  position: 'relative',
};

const moreMenu: CSSProperties = {
  position: 'absolute',
  right: 'var(--sp-2)',
  top: '100%',
  zIndex: 5,
  minWidth: 160,
  display: 'flex',
  flexDirection: 'column',
  padding: 'var(--sp-1)',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0,0,0,0.35))',
};

interface ITab {
  mode: RightPanelMode;
  label: string;
  title?: string;
}

/**
 * How many tabs fit on one row next to a "More" button, from measured widths.
 * Pure, so the overflow rule is testable without a layout engine.
 */
export function fitTabs(widths: number[], available: number, moreWidth: number, gap = 4): number {
  if (widths.length === 0) return 0;
  let used = 0;
  for (let i = 0; i < widths.length; i++) {
    const w = (widths[i] ?? 0) + (i > 0 ? gap : 0);
    const isLast = i === widths.length - 1;
    const reserve = isLast ? 0 : moreWidth + gap;
    if (used + w + reserve > available) return i;
    used += w;
  }
  return widths.length;
}

/**
 * Top-level right-panel container. Dispatches on `panel` and includes a
 * compact tabstrip so the user can switch modes without navigating away
 * from the canvas. `panel=node` only makes sense with a `nodeKey`; the
 * inspector panel itself handles the missing-key case so the URL alone
 * dictates state.
 */
export function RightPanel({
  panel,
  nodeKey,
  edgeId,
  runId,
  onChangePanel,
}: {
  panel: RightPanelMode;
  nodeKey: string | null;
  edgeId: string | null;
  runId: string | null;
  onChangePanel: (
    next: RightPanelMode,
    extras?: { nodeKey?: string; edgeId?: string; runId?: string },
  ) => void;
}) {
  const { workflow } = useWorkflowEditor();
  const tabs: ITab[] = rightPanelModes
    // `node` and `edge` are contextual — only show when a selection
    // is pinned to the URL. Otherwise the tabstrip would surface
    // tabs that error / show empty state.
    .filter((m) => (m === 'node' ? !!nodeKey : m === 'edge' ? !!edgeId : true))
    .map((mode) => ({
      mode,
      label:
        mode === 'node' ? `Inspector: ${nodeKey}` : mode === 'edge' ? `Edge` : panelLabels[mode],
      // A managed workflow has no Run button; point at where its
      // runs come from.
      title: mode === 'schedules' && workflow.managedBy ? managedScheduleTabTitle : undefined,
    }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TabStrip tabs={tabs} active={panel} onSelect={(mode) => onChangePanel(mode)} />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {panel === 'overview' ? (
          <OverviewPanel />
        ) : panel === 'node' ? (
          <NodeInspectorPanel nodeKey={nodeKey} onDeleted={() => onChangePanel('overview')} />
        ) : panel === 'edge' ? (
          <EdgeInspectorPanel
            edgeId={edgeId}
            onRepin={(newEdgeId) => onChangePanel('edge', { edgeId: newEdgeId })}
            onClose={() => onChangePanel('overview')}
          />
        ) : panel === 'runs' ? (
          <RunsPanel runId={runId} onOpenRun={(id) => onChangePanel('runs', { runId: id })} />
        ) : panel === 'schedules' ? (
          <SchedulesPanel onOpenRun={(id) => onChangePanel('runs', { runId: id })} />
        ) : panel === 'schemas' ? (
          <SchemasPanel nodeKey={nodeKey} />
        ) : (
          <VersionsPanel />
        )}
      </div>
    </div>
  );
}

/**
 * The tab row. Every tab is rendered once invisibly to measure it, then as
 * many as fit on one line are shown and the rest go behind "More". The active
 * tab is always visible: when it falls past the cut it swaps with the last
 * visible one.
 */
function TabStrip({
  tabs,
  active,
  onSelect,
}: {
  tabs: ITab[];
  active: RightPanelMode;
  onSelect: (mode: RightPanelMode) => void;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [menuOpen, setMenuOpen] = useState(false);
  const moreWidth = 64;

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    const compute = () => {
      const widths = Array.from(measure.children).map((el) => (el as HTMLElement).offsetWidth);
      const styles = getComputedStyle(row);
      const available =
        row.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
      if (!Number.isFinite(available) || available <= 0 || widths.every((w) => w === 0)) {
        // No layout (tests, hidden panel): show every tab.
        setVisibleCount(tabs.length);
        return;
      }
      setVisibleCount(fitTabs(widths, available, moreWidth));
    };
    compute();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(compute);
    observer.observe(row);
    return () => observer.disconnect();
  }, [tabs]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpen]);

  const count = Math.min(visibleCount, tabs.length);
  let visible = tabs.slice(0, count);
  let overflow = tabs.slice(count);
  const activeIdx = tabs.findIndex((t) => t.mode === active);
  if (activeIdx >= count && count > 0) {
    const activeTab = tabs[activeIdx];
    const last = visible[count - 1];
    if (activeTab && last) {
      visible = [...visible.slice(0, count - 1), activeTab];
      overflow = tabs.filter((t) => !visible.includes(t));
    }
  }

  return (
    <div ref={rowRef} style={tabRow} role="tablist" aria-label="Workflow detail panels">
      {visible.map((tab) => (
        <PanelTab
          key={tab.mode}
          active={active === tab.mode}
          label={tab.label}
          title={tab.title}
          onClick={() => onSelect(tab.mode)}
        />
      ))}
      {overflow.length > 0 ? (
        <>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`More panels (${overflow.length})`}
            title={overflow.map((t) => t.label).join(', ')}
            style={{ marginLeft: 'auto', borderRadius: 0, whiteSpace: 'nowrap' }}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            More ▾
          </button>
          {menuOpen ? (
            <div role="menu" style={moreMenu} onClick={(e) => e.stopPropagation()}>
              {overflow.map((tab) => (
                <button
                  key={tab.mode}
                  type="button"
                  role="menuitem"
                  className="btn btn--ghost btn--sm"
                  title={tab.title}
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => {
                    setMenuOpen(false);
                    onSelect(tab.mode);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
      {/* Measuring copy: every tab at its natural width, out of flow and unseen. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          gap: 'var(--sp-1)',
          left: 0,
          top: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {tabs.map((tab) => (
          <PanelTab key={tab.mode} active={false} label={tab.label} onClick={() => undefined} />
        ))}
      </div>
    </div>
  );
}

function PanelTab({
  active,
  label,
  title,
  onClick,
}: {
  active: boolean;
  label: string;
  title?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      title={title}
      className="btn btn--ghost btn--sm"
      onClick={onClick}
      style={{
        borderRadius: 0,
        borderBottom: `2px solid ${active ? 'var(--horizon-glow)' : 'transparent'}`,
        color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
        marginBottom: -1,
        whiteSpace: 'nowrap',
        flex: 'none',
      }}
    >
      {label}
    </button>
  );
}

/**
 * Pure helper: pick the panel mode given the route's raw search params.
 * Extracted so the route's `validateSearch` and the dispatcher agree on
 * the same normalization rules — and so we can unit-test it without
 * mounting the tree.
 */
export function resolvePanelMode(raw: string | undefined): RightPanelMode {
  if (raw && (rightPanelModes as readonly string[]).includes(raw)) {
    return raw as RightPanelMode;
  }
  return 'overview';
}
